"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ContactForm() {
  return <section id="contact-form" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-background">
      {}
      <div className="fixed inset-0 pointer-events-none opacity-5" style={{
      backgroundImage: `repeating-linear-gradient(
            45deg,
            transparent,
            transparent 40px,
            var(--color-muted) 40px,
            var(--color-muted) 42px
          )`
    }} />

      <div className="relative z-10 w-full max-w-lg px-6 py-12">
        <FormProvider className="space-y-8" formId="contact_form_contact" sectionName="contact-form" defaultValues={{
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: ""
      }} validationRules={{
        name: {
          required: true,
          minLength: 2,
          maxLength: 100
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          pattern: "phone"
        },
        message: {
          required: true,
          minLength: 10
        }
      }} submitText="Send Message" successMessage="Thank you for reaching out! We'll get back to you shortly." errorMessage="Unable to send your message. Please try again.">
          <ContactFormInner />
        </FormProvider>
      </div>
    </section>;
}
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const {
    ref: formRef,
    isVisible: formVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.1
  });
  if (isSubmitted && !submitError) {
    return <div className="glass-effect p-8 text-center space-y-6 animate-fade-in-up">
        <div className="space-y-3">
          <div className="size-16 mx-auto rounded-full bg-accent/20 flex items-center justify-center">
            <svg className="size-8 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-foreground text-lg font-medium">{successMessage}</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="w-full">
          Send Another Message
        </Button>
      </div>;
  }
  return <div ref={formRef} className="space-y-8">
      <div className={`text-center space-y-3 transition-all duration-[var(--duration-fast)] ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
        <h1 className="text-4xl font-display font-bold text-foreground tracking-tight">
          Contact Form
        </h1>
        <p className="text-muted-foreground text-lg">
          We'd love to hear from you. Send us a message and we'll respond as soon as possible.
        </p>
      </div>

      <Form onSubmit={handleSubmit} className="glass-effect p-8 space-y-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        },
        maxLength: {
          value: 100,
          message: "Name must not exceed 100 characters"
        }
      }} render={({
        field
      }) => <FormItem className={`transition-all duration-[var(--duration-fast)] ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`} style={{
        transitionDelay: "100ms"
      }}>
              <FormLabel>Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className="focus:border-accent transition-all duration-[var(--duration-fast)]" />
              </FormControl>
              <FormDescription>First and last name</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem className={`transition-all duration-[var(--duration-fast)] ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`} style={{
        transitionDelay: "150ms"
      }}>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className="focus:border-accent transition-all duration-[var(--duration-fast)]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[\d\s\-\(\)]+$/,
          message: "Invalid phone number format"
        }
      }} render={({
        field
      }) => <FormItem className={`transition-all duration-[var(--duration-fast)] ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`} style={{
        transitionDelay: "200ms"
      }}>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" {...field} className="focus:border-accent transition-all duration-[var(--duration-fast)]" />
              </FormControl>
              <FormDescription>Optional – we may call you back</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="subject" render={({
        field
      }) => <FormItem className={`transition-all duration-[var(--duration-fast)] ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`} style={{
        transitionDelay: "250ms"
      }}>
              <FormLabel>Subject</FormLabel>
              <FormControl>
                <Input placeholder="Brief subject of your inquiry" {...field} className="focus:border-accent transition-all duration-[var(--duration-fast)]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Message is required",
        minLength: {
          value: 10,
          message: "Message must be at least 10 characters"
        }
      }} render={({
        field
      }) => <FormItem className={`transition-all duration-[var(--duration-fast)] ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`} style={{
        transitionDelay: "300ms"
      }}>
              <FormLabel>Message</FormLabel>
              <FormControl>
                <Textarea placeholder="Tell us more about your project..." {...field} className="min-h-32 focus:border-accent transition-all duration-[var(--duration-fast)]" />
              </FormControl>
              <FormDescription>Provide as much detail as possible</FormDescription>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-md animate-fade-in-up">
            <p className="text-destructive text-sm font-medium">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className={`w-full btn-primary transition-all duration-[var(--duration-fast)] ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`} style={{
        transitionDelay: "350ms"
      }}>
          {isSubmitting ? <>
              <Loader className="animate-spin size-4" />
              Sending...
            </> : "Send Message"}
        </Button>
      </Form>
    </div>;
}